import { Injectable, NgZone, PLATFORM_ID, Inject } from '@angular/core';
import { MOBILE_BRIDGE_EVENT_RECEIVE, MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';
import { environment } from '@environments/environment';
import { Observable, Subject } from 'rxjs';
import { DefaultPresetColorCodes, presetMap } from '@modules/edit-preference/components/whitelabel-site/whitelabel-site-constant';
import { Application } from '@enums/application.enums';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

// Extend the Window interface to include ReactNativeWebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

interface IPermissions {
  imageVideoPicker?: boolean;
  allFilesPicker?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class MobileBridgeService {
  public messageSubject = new Subject<any>();
  public isMobileBridgeAvailable = false;
  public isMobileApp = false;
  public deviceType = ''
  public deviceTheme = '';
  public redirectTo = '';
  public paddingTop = 0;
  public permissions: IPermissions = {};
  public isIos = false;
  private isHardReloadInProgress = false;
  presetMap = {
    ...presetMap,
    default: {
      ...DefaultPresetColorCodes,
      theme_color: Application.DEFAULT_THEME_COLOR
    }
  }

  constructor(
    private ngZone: NgZone,
     private router : Router,
    @Inject(PLATFORM_ID) private platformId: Object) {
      if (isPlatformBrowser(this.platformId)) {
        this.initializeMessageListeners();
      }
  }

  initializeMessageListeners() {
     if (typeof window !== 'undefined') {
      window.addEventListener('message', (event: MessageEvent) => {
        // Only accept messages from ReactN
        if (event.data) {
          let message;
          try {
            message = JSON.parse(event.data);
          } catch (e) {
            return;
          }
          switch(message.type){ 
            case MOBILE_BRIDGE_EVENT_RECEIVE.DEVICE_TYPE:
              this.isMobileApp = true;
              this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_ORIGIN, {webUrl : environment.websiteURL, apiUrl : environment.baseURL, version: environment.version});
              this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_AUTH_TOKEN, { token: JSON.parse(localStorage.getItem(Application.FM_USER))?.fm_user });
              this.deviceType = message.payload;
              this.isIos = message.payload === Application.IOS_SMALL;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.REDIRECT_TO:
              this.redirectTo = message.payload.redirectTo;
              this.redirectToUrl(message.payload);
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.DEFAULT_DEVICE_THEME:
              this.deviceTheme = message.payload;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.SET_TOP_PADDING:
              this.paddingTop = message.payload?.topPadding;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.PERMISSIONS:
              this.permissions = message.payload;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.CLEAR_CACHE_AND_HARD_RELOAD:
              this.handleClearCacheAndHardReload(message.payload);
              break;
            default:
             this.ngZone.run(() => {
                this.messageSubject.next(message);
            });
          }
        }
      });
      document.addEventListener('message', (event: MessageEvent) => {
        // Only accept messages from ReactN
        if (event.data) {
          let message;
          try {
            message = JSON.parse(event.data);
          } catch (e) {
            console.error('Invalid JSON received in MobileBridgeService:', event.data, e);
            return;
          }
          switch(message.type){ 
            case MOBILE_BRIDGE_EVENT_RECEIVE.DEVICE_TYPE:
              this.isMobileApp = true;
              this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_ORIGIN, {webUrl : environment.websiteURL, apiUrl : environment.baseURL, version: environment.version});
              this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_AUTH_TOKEN, { token: JSON.parse(localStorage.getItem(Application.FM_USER))?.fm_user });
              this.deviceType = message.payload;
              this.isIos = message.payload === Application.IOS_SMALL;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.REDIRECT_TO:
              this.redirectTo = message.payload.redirectTo;
              this.redirectToUrl(message.payload);
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.DEFAULT_DEVICE_THEME:
              this.deviceTheme = message.payload;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.PERMISSIONS:
              this.permissions = message.payload;
              break;
            case MOBILE_BRIDGE_EVENT_RECEIVE.CLEAR_CACHE_AND_HARD_RELOAD:
              this.handleClearCacheAndHardReload(message.payload);
              break;
            default:
              this.ngZone.run(() => {
                this.messageSubject.next(message);
              });
              break;
          }
        }
      });
    }
  }

  private async handleClearCacheAndHardReload(payload?: { time_stamp?: string }): Promise<void> {
    if (this.isHardReloadInProgress) {
      return;
    }
    this.isHardReloadInProgress = true;

    if (!environment.production) {
      console.log('[MobileBridge] CLEAR_CACHE_AND_HARD_RELOAD received', payload?.time_stamp);
    }
    
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
      console.log('[MobileBridge] Cache cleaned');
    } catch (error) {
      console.error('[MobileBridge] Cache cleanup failed:', error);
    } finally {
      window.location.reload();
    }
  }

  redirectToUrl(payload): void {
    const { redirectTo, hashtag } = payload;
    try {
      const url = new URL(redirectTo);
      const path = url.pathname;
      const queryParams = Object.fromEntries(url.searchParams.entries());
      if (hashtag) {
        this.router.navigate([path, `#${hashtag}`]);
      } else {
        this.router.navigate([path], { queryParams });
      }
    } catch (e) {
      console.error('Invalid redirectTo URL:', redirectTo, e);
    }
  }



  /**
   * Send a message to the React Native app
   * @param type The type of the message (string)
   * @param payload The payload of the message (any)
   */
  sendMessage(type: string, payload?: any): void;
  /**
   * @deprecated Use the (type, payload) signature instead.
   */
  sendMessage(message: string): void;
  sendMessage(type: string, payload?: any): void {
    if(this.isMobileApp){
      let messageToSend: any;
      if (payload !== undefined) {
        messageToSend = { type: type, payload };
      } else {
        // Backward compatibility: if only a string is passed, send as-is
        messageToSend = type;
      }
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
          typeof messageToSend === 'string' ? messageToSend : JSON.stringify(messageToSend)
        );
      }
    }
   
  }

  forceSendMessage(type: string, payload?: any) {
    const messageToSend = { type, payload };
    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(messageToSend));
    }
  }

  /**
   * Observable for messages received from React Native
   */
  onMessage(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  sendThemeCodes(theme: string) {
    let codes;
    if (!!localStorage.getItem(Application.IS_WHITE_LABEL)) {
      codes = {
        'theme_color': localStorage.getItem('theme_color'),
        'bg_color': localStorage.getItem('bg_color'),
        'secondary_bg_color': localStorage.getItem('secondary_bg_color'),
        'post_text_color': localStorage.getItem('post_text_color'),
        'button_text_color': localStorage.getItem('button_text_color'),
        'bg_text_color': localStorage.getItem('bg_text_color')
      }
    } else {
      codes = this.presetMap[theme.replace('theme-', '')];
    }
    this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.CHANGE_THEME, codes);
  }

  sendLaunchScreenMessage() {
    this.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SHOW_LAUNCH, {});
  }
}